import type {
  accountTypes,
  controlCenterPlatform,
  countries,
  dealerCodes,
  dealerSearchIncoming,
  states,
  statuses,
  verticalTypes
} from "@dealer-center/ddc-pages-account-search-interface";
import type {
  ComboBoxEventValue,
  DatePickerEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import { TagManager } from "@dealer-center/core-third-party-gtm";
import { stringToDate, stringToDateString } from "./helper";
import { getSelectedOnChange } from "./generic";

export const getDateBeforeOnChange = (
  setDateBeforeValue: (value: Date | undefined) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<DatePickerEventValue>) => {
    setDateBeforeValue(stringToDate(event.target.value?.dateValue));
    const before: string = stringToDateString(event.target.value?.dateValue);
    const created = {
      after: filters.created?.after || "",
      before
    };
    TagManager.event({
      eventName: "Created Before Filter",
      eventProperties: {
        component: "client-search",
        actionValue: before
      }
    });
    updateSearchRequest({ created });
  };
};

export const getDateAfterOnChange = (
  setDateAfterValue: (value: Date | undefined) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<DatePickerEventValue>) => {
    setDateAfterValue(stringToDate(event.target.value?.dateValue));
    const after: string = stringToDateString(event.target.value?.dateValue);
    const created = {
      after,
      before: filters.created?.before || ""
    };
    TagManager.event({
      eventName: "Created After Filter",
      eventProperties: {
        component: "client-search",
        actionValue: after
      }
    });
    updateSearchRequest({ created });
  };
};

export const getStatusesOnChange = (
  setStatusValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setStatusValue(event.target.value);
    const statusesResult = getSelectedOnChange(event) as statuses[];
    TagManager.event({
      eventName: "Statuses Filter",
      eventProperties: {
        component: "client-search",
        actionValue: statusesResult?.join(", ")
      }
    });
    updateSearchRequest({ statuses: statusesResult });
  };
};
export const getCountryOnChange = (
  setSelectedCountryValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setSelectedCountryValue(event.target.value);
    const countriesResult = getSelectedOnChange(event) as countries[];
    TagManager.event({
      eventName: "Country Filter",
      eventProperties: {
        component: "client-search",
        actionValue: countriesResult?.join(", ")
      }
    });
    updateSearchRequest({ countries: countriesResult });
  };
};
export const getfranchisesMatchAllOnChange = (
  setFranchisesMatchAllValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setFranchisesMatchAllValue(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (!Array.isArray(itemsInArray)) {
      itemsInArray = [itemsInArray];
    }
    const matchAll = itemsInArray?.map(String);
    const { matchAny, matchNone } = filters.franchises || {};
    TagManager.event({
      eventName: "Franchises Match All",
      eventProperties: {
        component: "client-search",
        actionValue: matchAll?.join(", ")
      }
    });
    updateSearchRequest({
      franchises: { matchAny, matchAll, matchNone }
    });
  };
};
export const getfranchisesMatchAnyOnChange = (
  setFranchisesMatchAnyValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setFranchisesMatchAnyValue(event.target.value);
    let selectedOnChange = getSelectedOnChange(event) || [];
    if (!Array.isArray(selectedOnChange)) {
      selectedOnChange = [selectedOnChange];
    }
    const matchAny = selectedOnChange?.map(String);
    const { matchAll, matchNone } = filters.franchises || {};
    TagManager.event({
      eventName: "Franchises Match Any",
      eventProperties: {
        component: "client-search",
        actionValue: matchAny?.join(", ")
      }
    });
    updateSearchRequest({
      franchises: { matchAny, matchAll, matchNone }
    });
  };
};
export const getfranchisesMatchNoneOnChange = (
  setFranchisesMatchNoneValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setFranchisesMatchNoneValue(event.target.value);
    let selectedOnChange = getSelectedOnChange(event) || [];
    if (!Array.isArray(selectedOnChange)) {
      selectedOnChange = [selectedOnChange];
    }
    const matchNone = selectedOnChange?.map(String);
    const { matchAll, matchAny } = filters.franchises || {};
    TagManager.event({
      eventName: "Franchises Match None",
      eventProperties: {
        component: "client-search",
        actionValue: matchNone?.join(", ")
      }
    });
    updateSearchRequest({
      franchises: { matchAny, matchAll, matchNone }
    });
  };
};
export const getdealerCodesValueMatchAllOnChange = (
  setDealerCodesValueMatchAll: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setDealerCodesValueMatchAll(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (Array.isArray(itemsInArray)) {
      if (itemsInArray.length > 1) {
        itemsInArray = itemsInArray[1];
      } else if (itemsInArray.length === 1) {
        itemsInArray = itemsInArray[0];
      } else if (itemsInArray.length === 0) {
        itemsInArray = "*";
      }
    }
    const onChangeItem = {
      type: filters.dealerCodes?.matchAll?.[0]?.type || "*",
      codes: [itemsInArray.toString() || "*"]
    };
    if (
      onChangeItem.type === "*" &&
      onChangeItem.codes[0] === "" &&
      onChangeItem.codes.length === 1
    ) {
      updateSearchRequest({
        dealerCodes: {
          matchAll: []
        }
      });
      return;
    }
    const matchAll: dealerCodes[] = [onChangeItem];
    TagManager.event({
      eventName: "dealerCodes Values Match All",
      eventProperties: {
        component: "client-search",
        actionValue: onChangeItem.codes[0]
      }
    });
    updateSearchRequest({
      dealerCodes: { matchAll }
    });
  };
};
export const getdealerCodesMatchAllOnChange = (
  setDealerCodesMatchAll: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setDealerCodesMatchAll(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (Array.isArray(itemsInArray)) {
      if (itemsInArray.length > 1) {
        itemsInArray = itemsInArray[1];
      } else if (itemsInArray.length === 1) {
        itemsInArray = itemsInArray[0];
      } else if (itemsInArray.length === 0) {
        itemsInArray = "*";
      }
    }
    const onChangeItem = {
      type: itemsInArray.toString() || "*",
      codes: [filters.dealerCodes?.matchAll?.[0]?.codes?.[0] || "*"]
    };
    if (
      onChangeItem.type === "*" &&
      onChangeItem.codes[0] === "*" &&
      onChangeItem.codes.length === 1
    ) {
      updateSearchRequest({
        dealerCodes: {
          matchAll: []
        }
      });
      return;
    }
    const matchAll: dealerCodes[] = [onChangeItem];
    TagManager.event({
      eventName: "dealerCodes Match All",
      eventProperties: {
        component: "client-search",
        actionValue: onChangeItem.type
      }
    });
    updateSearchRequest({
      dealerCodes: { matchAll }
    });
  };
};

export const getStatesOnChange = (
  setSelectedStateValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setSelectedStateValue(event.target.value);
    const statesValue = getSelectedOnChange(event) as states[];
    TagManager.event({
      eventName: "States Filter",
      eventProperties: {
        component: "client-search",
        actionValue: statesValue?.join(", ")
      }
    });
    updateSearchRequest({ states: statesValue });
  };
};

export const geturlOnChange = (
  setSelectedUrlValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setSelectedUrlValue(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (!Array.isArray(itemsInArray)) {
      itemsInArray = [itemsInArray];
    }
    const urls = itemsInArray?.map(String);
    TagManager.event({
      eventName: "Url Filter",
      eventProperties: {
        component: "client-search",
        actionValue: urls?.join(", ")
      }
    });
    updateSearchRequest({ urls });
  };
};
export const getcontrolCenterPlatformnsChange = (
  setControlCenterPlatformsValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setControlCenterPlatformsValue(event.target.value);
    const controlCenterPlatforms = getSelectedOnChange(
      event
    ) as controlCenterPlatform[];
    TagManager.event({
      eventName: "ControlCenter Platform Filter",
      eventProperties: {
        component: "client-search",
        actionValue: controlCenterPlatforms?.join(", ")
      }
    });
    updateSearchRequest({ controlCenterPlatforms });
  };
};
export const getresourcesMatchAllOnChange = (
  setResourcesMatchAllValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setResourcesMatchAllValue(event.target.value);
    let matchAll = getSelectedOnChange(event) || [];
    if (!Array.isArray(matchAll)) {
      matchAll = [matchAll];
    }
    const { matchAny, matchNone } = filters.resources || {};
    TagManager.event({
      eventName: "Resources Match All",
      eventProperties: {
        component: "client-search",
        actionValue: matchAll?.join(", ")
      }
    });
    updateSearchRequest({
      resources: { matchAny, matchAll, matchNone }
    });
  };
};
export const getresourcesMatchAnyOnChange = (
  setResourcesMatchAnyValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setResourcesMatchAnyValue(event.target.value);
    let matchAny = getSelectedOnChange(event) || [];
    if (!Array.isArray(matchAny)) {
      matchAny = [matchAny];
    }
    const { matchAll, matchNone } = filters.resources || {};
    TagManager.event({
      eventName: "Resources Match Any",
      eventProperties: {
        component: "client-search",
        actionValue: matchAny?.join(", ")
      }
    });
    updateSearchRequest({
      resources: { matchAny, matchAll, matchNone }
    });
  };
};
export const getresourcesMatchNoneOnChange = (
  setResourcesMatchNoneValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void,
  filters: dealerSearchIncoming
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setResourcesMatchNoneValue(event.target.value);
    let matchNone = getSelectedOnChange(event) || [];
    if (!Array.isArray(matchNone)) {
      matchNone = [matchNone];
    }
    const { matchAll, matchAny } = filters.resources || {};
    TagManager.event({
      eventName: "Resources Match None",
      eventProperties: {
        component: "client-search",
        actionValue: matchNone?.join(", ")
      }
    });
    updateSearchRequest({
      resources: { matchAny, matchAll, matchNone }
    });
  };
};
export const getidOnChange = (
  setIdValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setIdValue(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (!Array.isArray(itemsInArray)) {
      itemsInArray = [itemsInArray];
    }
    const ids = itemsInArray?.map(String);
    TagManager.event({
      eventName: "Id Filter",
      eventProperties: {
        component: "client-search",
        actionValue: ids?.join(", ")
      }
    });
    updateSearchRequest({ ids });
  };
};

export const getverticalTypesnChange = (
  setVerticalTypesValue: (value: ComboBoxEventValue) => void,

  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setVerticalTypesValue(event.target.value);
    const verticalTypesResult = getSelectedOnChange(event) as verticalTypes[];
    TagManager.event({
      eventName: "Vertical Type Filter",
      eventProperties: {
        component: "client-search",
        actionValue: verticalTypesResult?.join(", ")
      }
    });
    updateSearchRequest({ verticalTypes: verticalTypesResult });
  };
};
export const getpostalCodeOnChange = (
  setPostalCodeValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setPostalCodeValue(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (!Array.isArray(itemsInArray)) {
      itemsInArray = [itemsInArray];
    }
    const postalCodes = itemsInArray?.map(String);
    TagManager.event({
      eventName: "Postal Code Filter",
      eventProperties: {
        component: "client-search",
        actionValue: postalCodes?.join(", ")
      }
    });
    updateSearchRequest({ postalCodes });
  };
};
export const getaccountTypesOnChange = (
  setAccountTypesValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setAccountTypesValue(event.target.value);
    const accountTypeValue = getSelectedOnChange(event) as accountTypes[];
    TagManager.event({
      eventName: "Account Type Filter",
      eventProperties: {
        component: "client-search",
        actionValue: accountTypeValue?.join(", ")
      }
    });
    updateSearchRequest({ accountTypes: accountTypeValue });
  };
};
export const getnameOnChange = (
  setNameValue: (value: ComboBoxEventValue) => void,
  updateSearchRequest: (value: dealerSearchIncoming) => void
) => {
  return (event: InterstateOnChangeEvent<ComboBoxEventValue>) => {
    setNameValue(event.target.value);
    let itemsInArray = getSelectedOnChange(event) || [];
    if (!Array.isArray(itemsInArray)) {
      itemsInArray = [itemsInArray];
    }
    const names = itemsInArray?.map(String);
    TagManager.event({
      eventName: "Name Filter",
      eventProperties: {
        component: "client-search",
        actionValue: names?.join(", ")
      }
    });
    updateSearchRequest({ names });
  };
};

export default {
  getDateBeforeOnChange,
  getDateAfterOnChange,
  getStatusesOnChange,
  getCountryOnChange,
  getfranchisesMatchAllOnChange,
  getfranchisesMatchAnyOnChange,
  getfranchisesMatchNoneOnChange,
  getdealerCodesValueMatchAllOnChange,
  getdealerCodesMatchAllOnChange,
  getStatesOnChange,
  geturlOnChange,
  getcontrolCenterPlatformnsChange,
  getresourcesMatchAllOnChange,
  getresourcesMatchAnyOnChange,
  getresourcesMatchNoneOnChange,
  getidOnChange,
  getverticalTypesnChange,
  getpostalCodeOnChange,
  getaccountTypesOnChange,
  getnameOnChange
};
